import type { WsClientMessage, WsServerMessage } from "../generated-social"

export type Status = "idle" | "connecting" | "open" | "backoff" | "suspended"
export type SuspendReason = "hidden" | "offline" | "superseded" | "auth"

export interface Snapshot {
	status: Status
	suspendReason: SuspendReason | null
	isConnected: boolean
	reconnectAttempts: number
	userId: string | null
}

export interface Deps {
	createSocket(token: string): WebSocket
	getToken(): Promise<string | undefined>
	isAppActive(): boolean
	onAppActiveChange(handler: (isActive: boolean) => void): () => void
	isOnline(): boolean
	onOnlineChange(handler: (isOnline: boolean) => void): () => void
	capture(event: string, properties: Record<string, unknown>): void
	captureException(error: unknown, properties: Record<string, unknown>): void
}

export interface ConnectionEvents {
	state: (snapshot: Snapshot) => void
	message: (payload: WsServerMessage) => void
	error: (event: Event) => void
}

export interface Connection {
	connect(userId: string): Promise<void>
	disconnect(): void
	send(payload: WsClientMessage): boolean
	on<T extends keyof ConnectionEvents>(
		event: T,
		handler: ConnectionEvents[T],
	): () => void
	snapshot(): Snapshot
}

type FailureCause = "close" | "token" | "timeout" | "create"

interface Failure {
	cause: FailureCause
	code: number | null
	reason: string
	wasClean: boolean | null
}

const BASE_DELAY = 1000
const MAX_DELAY = 30_000
const IDLE_TIMEOUT = 75_000
const IDLE_CHECK = 15_000
const MIN_UPTIME = 15_000
const FIRST_CONNECT_TIMEOUT = 5000
const CONNECT_TIMEOUT = 15_000
const RESUME_MIN_INTERVAL = 1000
const SUPERSEDED_WAKE_INTERVAL = 60_000

const NORMAL_CLOSE = 1000
const GOING_AWAY = 1001
const IDLE_CLOSE = 4000
const SUPERSEDED_CLOSE = 4006
const REPLACED_REASON = "replaced by new session"

function isSupersededClose(code: number, reason: string): boolean {
	return (
		code === SUPERSEDED_CLOSE ||
		(code === GOING_AWAY && reason === REPLACED_REASON)
	)
}

export function createConnection(deps: Deps): Connection {
	let userId: string | null = null
	let status: Status = "idle"
	let suspendReason: SuspendReason | null = null
	let sock: WebSocket | null = null
	let attemptId = 0
	let attempt = 0
	let paused = false
	let openedAt: number | null = null
	let lastMsgAt = 0
	let lastResumeAt = 0
	let lastSupersededWakeAt = 0
	let activeSince = Date.now()
	let downSince: number | null = null
	let streak = 0
	let retryTimer: ReturnType<typeof setTimeout> | null = null
	let connectTimer: ReturnType<typeof setTimeout> | null = null
	let idleTimer: ReturnType<typeof setInterval> | null = null
	let joining: Promise<void> | null = null
	let unwatch: (() => void) | null = null

	const handlers: { [K in keyof ConnectionEvents]: ConnectionEvents[K][] } = {
		state: [],
		message: [],
		error: [],
	}

	function snapshot(): Snapshot {
		let visibleSuspendReason: Snapshot["suspendReason"] = null
		if (status === "suspended") visibleSuspendReason = suspendReason
		return {
			status,
			suspendReason: visibleSuspendReason,
			isConnected: status === "open",
			reconnectAttempts: attempt,
			userId,
		}
	}

	let lastSnapshot = snapshot()

	function emitState(): void {
		const next = snapshot()
		if (
			next.status === lastSnapshot.status &&
			next.suspendReason === lastSnapshot.suspendReason &&
			next.reconnectAttempts === lastSnapshot.reconnectAttempts &&
			next.userId === lastSnapshot.userId
		) {
			return
		}
		lastSnapshot = next
		for (const handler of handlers.state) {
			handler(next)
		}
	}

	function clearRetry(): void {
		if (retryTimer !== null) {
			clearTimeout(retryTimer)
			retryTimer = null
		}
	}

	function clearConnect(): void {
		if (connectTimer !== null) {
			clearTimeout(connectTimer)
			connectTimer = null
		}
	}

	function stopIdle(): void {
		if (idleTimer !== null) {
			clearInterval(idleTimer)
			idleTimer = null
		}
	}

	function startIdle(): void {
		stopIdle()
		idleTimer = setInterval(() => {
			if (sock === null || sock.readyState !== WebSocket.OPEN) {
				return
			}
			if (Date.now() - lastMsgAt > IDLE_TIMEOUT) {
				sock.close(IDLE_CLOSE, "idle timeout")
			}
		}, IDLE_CHECK)
	}

	function nextDelay(): number {
		const base = Math.min(BASE_DELAY * 2 ** attempt, MAX_DELAY)
		const jitter = 0.5 + Math.random() * 0.5
		return Math.floor(base * jitter)
	}

	function detach(reason: string): void {
		const current = sock
		sock = null
		clearConnect()
		stopIdle()
		if (current === null) {
			return
		}
		current.onopen = null
		current.onmessage = null
		current.onclose = null
		current.onerror = null
		const isAlive =
			current.readyState === WebSocket.CONNECTING ||
			current.readyState === WebSocket.OPEN
		if (isAlive) {
			current.close(NORMAL_CLOSE, reason)
		}
	}

	function suspend(reason: SuspendReason): void {
		clearRetry()
		clearConnect()
		stopIdle()
		if (downSince !== null) {
			deps.capture("ws_suspended", {
				suspend_reason: reason,
				attempts: streak,
				downtime_s: Math.round((Date.now() - downSince) / 1000),
			})
		}
		status = "suspended"
		suspendReason = reason
		downSince = null
		streak = 0
		emitState()
	}

	function uptimeAt(now: number): number {
		if (openedAt === null) return 0
		return now - openedAt
	}

	function routeFailure(failure: Failure): void {
		detach("failure")
		const now = Date.now()
		const lived = uptimeAt(now)
		openedAt = null
		if (downSince === null) {
			downSince = now
			deps.capture("ws_connect_failed", {
				cause: failure.cause,
				code: failure.code,
				reason: failure.reason,
				was_clean: failure.wasClean,
				lived_s: Math.round(lived / 1000),
				since_active_s: Math.round((now - activeSince) / 1000),
				online: deps.isOnline(),
				app_active: !paused,
			})
		}
		streak += 1
		if (lived < MIN_UPTIME) attempt += 1
		else attempt = 0
		if (paused) {
			suspend("hidden")
			return
		}
		if (!deps.isOnline()) {
			suspend("offline")
			return
		}
		status = "backoff"
		suspendReason = null
		retryTimer = setTimeout(() => {
			retryTimer = null
			void startAttempt()
		}, nextDelay())
		emitState()
	}

	function handleMessage(current: WebSocket, data: unknown): void {
		let payload: WsServerMessage
		try {
			payload = JSON.parse(String(data))
		} catch (error) {
			console.error("[WS] Failed to parse message:", error)
			deps.captureException(error, { scope: "ws.parse" })
			return
		}
		if (payload.type === "system.ping") {
			if (current.readyState === WebSocket.OPEN) {
				current.send(
					JSON.stringify({
						payload: {
							type: "system.pong",
							message: null,
						} satisfies WsClientMessage,
					}),
				)
			}
			return
		}
		for (const handler of handlers.message) {
			handler(payload)
		}
	}

	function bind(current: WebSocket): void {
		current.onopen = () => {
			if (sock !== current) {
				return
			}
			clearConnect()
			const now = Date.now()
			openedAt = now
			lastMsgAt = now
			status = "open"
			suspendReason = null
			if (!paused) {
				startIdle()
			}
			if (downSince !== null) {
				deps.capture("ws_recovered", {
					attempts: streak,
					downtime_s: Math.round((now - downSince) / 1000),
				})
				downSince = null
				streak = 0
			}
			emitState()
		}

		current.onmessage = (event) => {
			if (sock !== current) {
				return
			}
			lastMsgAt = Date.now()
			handleMessage(current, event.data)
		}

		current.onerror = (event) => {
			if (sock !== current) {
				return
			}
			for (const handler of handlers.error) {
				handler(event)
			}
		}

		current.onclose = (event) => {
			if (sock !== current) {
				return
			}
			if (isSupersededClose(event.code, event.reason)) {
				const uptime = uptimeAt(Date.now())
				detach("superseded")
				openedAt = null
				deps.capture("ws_superseded", {
					uptime_s: Math.round(uptime / 1000),
				})
				suspend("superseded")
				return
			}
			routeFailure({
				cause: "close",
				code: event.code,
				reason: event.reason,
				wasClean: event.wasClean,
			})
		}
	}

	function openSocket(token: string): void {
		let created: WebSocket
		try {
			created = deps.createSocket(token)
		} catch (error) {
			console.error("[WS] Failed to create connection:", error)
			routeFailure({
				cause: "create",
				code: null,
				reason: String(error),
				wasClean: null,
			})
			return
		}
		sock = created
		bind(created)
		let timeout = CONNECT_TIMEOUT
		if (attempt === 0) timeout = FIRST_CONNECT_TIMEOUT
		connectTimer = setTimeout(() => {
			connectTimer = null
			if (sock !== created) {
				return
			}
			routeFailure({
				cause: "timeout",
				code: null,
				reason: "connect timeout",
				wasClean: null,
			})
		}, timeout)
	}

	function tokenResolved(token: string | undefined): void {
		if (status !== "connecting") {
			return
		}
		if (token === undefined) {
			suspend("auth")
			return
		}
		openSocket(token)
	}

	function tokenFailed(error: unknown): void {
		if (status !== "connecting") {
			return
		}
		let reason = String(error)
		if (error instanceof Error) reason = error.message
		routeFailure({
			cause: "token",
			code: null,
			reason,
			wasClean: null,
		})
	}

	function startAttempt(): Promise<void> {
		attemptId += 1
		const id = attemptId
		clearRetry()
		clearConnect()
		status = "connecting"
		suspendReason = null
		emitState()
		const promise = deps
			.getToken()
			.then(
				(token) => {
					if (id === attemptId) {
						tokenResolved(token)
					}
				},
				(error: unknown) => {
					if (id === attemptId) {
						tokenFailed(error)
					}
				},
			)
			.finally(() => {
				if (joining === promise) {
					joining = null
				}
			})
		joining = promise
		return promise
	}

	function restart(): void {
		detach("restart")
		openedAt = null
		attempt = 0
		void startAttempt()
	}

	function resume(resetAttempt: boolean): void {
		if (userId === null) {
			return
		}
		const now = Date.now()
		if (now - lastResumeAt < RESUME_MIN_INTERVAL) {
			return
		}
		lastResumeAt = now
		if (status === "open") {
			if (now - lastMsgAt < IDLE_TIMEOUT) {
				if (!paused) {
					startIdle()
				}
				return
			}
			restart()
			return
		}
		if (status !== "backoff" && status !== "suspended") {
			return
		}
		clearRetry()
		if (resetAttempt) {
			attempt = 0
		}
		void startAttempt()
	}

	function onAppActive(isActive: boolean): void {
		paused = !isActive
		if (!isActive) {
			stopIdle()
			if (status === "backoff") {
				suspend("hidden")
			}
			return
		}
		activeSince = Date.now()
		if (status === "suspended" && suspendReason === "superseded") {
			const now = Date.now()
			if (now - lastSupersededWakeAt < SUPERSEDED_WAKE_INTERVAL) {
				return
			}
			lastSupersededWakeAt = now
			resume(true)
			return
		}
		if (
			status === "suspended" &&
			suspendReason === "offline" &&
			!deps.isOnline()
		) {
			return
		}
		resume(status !== "backoff")
	}

	function onOnline(isOnline: boolean): void {
		if (!isOnline) {
			if (status === "backoff") {
				suspend("offline")
			}
			return
		}
		if (status === "suspended" && suspendReason === "offline") {
			resume(true)
			return
		}
		if (status === "backoff") {
			resume(false)
		}
	}

	function watch(): void {
		unwatch?.()
		const offActive = deps.onAppActiveChange(onAppActive)
		const offOnline = deps.onOnlineChange(onOnline)
		unwatch = () => {
			offActive()
			offOnline()
		}
	}

	function teardown(reason: string): void {
		attemptId += 1
		clearRetry()
		detach(reason)
		openedAt = null
		downSince = null
		streak = 0
		attempt = 0
		joining = null
	}

	function connect(nextUserId: string): Promise<void> {
		if (userId === null) {
			userId = nextUserId
			watch()
			paused = !deps.isAppActive()
			activeSince = Date.now()
			attempt = 0
			return startAttempt()
		}
		if (userId !== nextUserId) {
			teardown("User disconnect")
			userId = nextUserId
			return startAttempt()
		}
		if (status === "connecting") {
			return joining ?? Promise.resolve()
		}
		if (status === "suspended") {
			if (suspendReason === "auth") {
				resume(true)
			} else if (suspendReason === "offline" && deps.isOnline()) {
				resume(true)
			} else if (suspendReason === "hidden" && deps.isAppActive()) {
				paused = false
				resume(true)
			}
			return joining ?? Promise.resolve()
		}
		return Promise.resolve()
	}

	function disconnect(): void {
		teardown("User disconnect")
		unwatch?.()
		unwatch = null
		userId = null
		status = "idle"
		suspendReason = null
		emitState()
	}

	function send(payload: WsClientMessage): boolean {
		if (sock === null || sock.readyState !== WebSocket.OPEN) {
			return false
		}
		try {
			sock.send(JSON.stringify({ payload }))
			return true
		} catch (error) {
			console.error("[WS] Failed to send message:", error)
			deps.captureException(error, { scope: "ws.send", type: payload.type })
			return false
		}
	}

	function on<T extends keyof ConnectionEvents>(
		event: T,
		handler: ConnectionEvents[T],
	): () => void {
		const list = handlers[event]
		list.push(handler)
		return () => {
			const index = list.indexOf(handler)
			if (index !== -1) {
				list.splice(index, 1)
			}
		}
	}

	return { connect, disconnect, send, on, snapshot }
}
